import { Tabs, Dropdown, Menu, Alert, Button } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { useMemo, useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { PageName } from "ui/PageName"
import { ProgressBar } from "ui/ProgressBar"
import { PageTitle } from "./PageTitle"
import styles from "./ProNewListingLayout.module.css"
import { proNewListingMenuList } from "./proNewListingMenuList"
import { getRouteMap } from "./routeMaps"
import { EllipsisIcon } from "icons"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import { routes } from "router"
import { useDispatch, useSelector } from "react-redux"
import { updateTrip } from "store/trips"
import { serialize } from "object-to-formdata"
import { toaster } from "components/common/Toast"
import { STRIPE_TAB } from "pagesV2/pro/Settings/ProSettings/PaymentProviders/constants"

export const ProNewListingLayout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const isPreviewMode = searchParams.get("readOnlyPreview")
  const { pathname } = useLocation()
  const { id: tripId } = useParams()
  const tabId = pathname.split("/").findLast(Boolean)
  const user = useSelector((store) => store.user)
  const trip = useSelector((state) => state.trips.trip) // Add trip selector
  const stripeData = user?.guide?.stripe_account_data || {}
  const payoutsEnabled = !!stripeData.payouts_enabled
  const chargesEnabled = !!stripeData.charges_enabled
  const noStripeError = !stripeData.error_message
  const isEditMode = pathname.includes("/edit/")
  const shouldShowStripeWarning = (!payoutsEnabled || !chargesEnabled || !noStripeError) && !isEditMode
  const [showStripeWarning, setShowStripeWarning] = useState(shouldShowStripeWarning)
  const forms = useSelector((state) => state.proNewListingForms) || {}

  const { id, percent, title, subTitle } = useMemo(() => {
    return proNewListingMenuList.find((tabItem) => tabItem.id === tabId) || proNewListingMenuList[0]
  }, [tabId])

  const scrollAnchorRef = useRef(null)

  useEffect(() => {
    if (scrollAnchorRef?.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "auto" })
    }
  }, [pathname])

  const onChange = (key) => {
    if (isPreviewMode || !tripId) return
    const mode = isEditMode ? searchParams.get("route") : "create"
    const routeFn = getRouteMap()[mode]?.[key]
    if (routeFn) {
      navigate(routeFn({ id: tripId }))
    }
  }

  const tabList = useMemo(() => {
    return proNewListingMenuList.map((item) => ({
      key: item.id,
      label: item.label
    }))
  }, [])

  const [visibleTabs, setVisibleTabs] = useState([])
  const tabListRef = useRef([])

  useEffect(() => {
    const handleResize = () => {
      updateVisibleTabs()
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const updateVisibleTabs = () => {
    const containerWidth = document.querySelector(".ant-tabs")?.offsetWidth
    let totalWidth = 0
    const visibleTabs = []

    for (const [index, tab] of tabListRef.current.entries()) {
      totalWidth += tab?.offsetWidth
      if (totalWidth <= containerWidth) {
        visibleTabs?.push(index + 1)
      }
    }

    setVisibleTabs(visibleTabs)
  }

  const handleDropdownClick = (key) => {
    onChange(key)
  }

  const getDropdownMenu = () => {
    const hiddenTabs = proNewListingMenuList.map((item) => item.id).filter((key) => key !== id && !visibleTabs.includes(key))

    return (
      <Menu onClick={(e) => handleDropdownClick(e.key)}>
        {hiddenTabs.map((key) => (
          <Menu.Item key={key}>{proNewListingMenuList?.find((item) => item?.id === key)?.label}</Menu.Item>
        ))}
      </Menu>
    )
  }

  const handleStripeNavigate = async () => {
    const touched = !!forms?.details?.touched
    if (!touched) {
      navigate(routes.proSettingsPaymentProviders({}, { tab: STRIPE_TAB }))
      return
    }

    const detailsValues = forms?.details?.values || {}
    const payload = { ...detailsValues }
    const formData = serialize({ trip: payload }, { allowEmptyArrays: true })

    await dispatch(updateTrip(tripId, formData))
      .then(() => {
        !isEditMode && toaster.success(t("experiences.stripe_modal.draft_saved"))
        navigate(routes.proSettingsPaymentProviders({}, { tab: STRIPE_TAB }))
      })
      .catch((error) => {
        console.log("error", error)
      })
  }

  const pageTitle = useMemo(() => {
    if (isEditMode) {
      return trip?.title ? (
        <span>
          {t("listings.edit_new")} · <span style={{ color: "var(--color-primary)" }}>{trip.title}</span>
        </span>
      ) : (
        t("listings.edit_new")
      )
    }
    return t("listings.create_new")
  }, [isEditMode, trip, t])

  return (
    <>
      <div ref={scrollAnchorRef} />
      <div className={styles.layout}>
        <div className={styles.header}>
          {isPreviewMode ? (
            <PageName title="Preview Listing" />
          ) : (
            <>
              {!isEditMode && <ProgressBar percent={percent} />}
              <PageName title={pageTitle} />
              {showStripeWarning && (
                <Alert
                  type="error"
                  className={styles.stripeWarningBar}
                  message={
                    <>
                      <WarningOutlinedWithBg className={styles.errorIcon} />
                      {t("booking.publishing_is_blocked")}&nbsp;
                      <Button type="button" className={styles.stripeWarningLink} onClick={() => handleStripeNavigate()}>
                        {t("booking.connect_stripe")}
                      </Button>
                      {t("booking.draft_saved")}
                      <CloseOutlined className={styles.stripeWarningClose} onClick={() => setShowStripeWarning(false)} />
                    </>
                  }
                ></Alert>
              )}

              <Tabs
                activeKey={id}
                items={tabList}
                onChange={onChange}
                tabBarExtraContent={
                  visibleTabs.length < proNewListingMenuList.length ? (
                    <Dropdown overlay={getDropdownMenu()} trigger={["click"]} className={`ml-xs ${styles.tabsMoreIcon}`}>
                      <EllipsisIcon fill="#ddd" height={20} width={20} />
                    </Dropdown>
                  ) : null
                }
              />
            </>
          )}
        </div>

        <div className={styles.body}>
          {!isPreviewMode && <PageTitle title={title} subTitle={subTitle} />}
          <div className={styles.pageContent}>
            <Outlet />
          </div>
        </div>
      </div>
    </>
  )
}
